/**
 * 数字分身工作室卡片墙(B5 第一刀,终稿屏⑦):快捷 4 卡(视频数字人 / 文生语音「即将」/
 * 声纹克隆 / 从爆款开始)+ 公共形象横排(平台策展安全资产,preset-avatars 真数据)+
 * 授权红线条。形象/声音库版式深改与口播工作台版式属 B5 二刀。
 */
import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, UserSquare2 } from "lucide-react";

import { DH_CARDS } from "@/entities/scene-card/catalog";
import { DH_LINE_ROUTE } from "@/entities/dh-profile/model/dhLines";
import type { SceneCard } from "@/entities/scene-card/model/types";
import { SceneCapCard } from "@/entities/scene-card/ui/SceneCapCard";
import { SceneHubHero } from "@/entities/scene-card/ui/SceneHubHero";
import { useModuleVisible } from "@/contexts/AuthContext";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { digitalHumanService } from "../../api/digitalHumanService";

function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-2">
      <span className="h-3.5 w-[3px] self-center rounded-sm bg-primary" />
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </div>
  );
}

export function DhHubSections() {
  const navigate = useNavigate();
  // 终稿屏⑨动效:卡墙入场 stagger(只动 transform/opacity;reduced-motion 自动跳过)。
  const cardsRef = useRef<HTMLDivElement>(null);
  const presetsRef = useRef<HTMLDivElement>(null);
  const explosiveVisible = useModuleVisible("ai_explosive_parse");

  const presetsQ = useQuery({
    queryKey: ["dh", "preset-avatars"],
    queryFn: digitalHumanService.listPresetAvatars,
    staleTime: 60_000,
    retry: false,
  });
  const presets = presetsQ.data ?? [];
  useStaggerReveal(cardsRef);
  useStaggerReveal(presetsRef, { deps: [presets.length] });

  const open = (card: SceneCard) => {
    if (!card.route) return;
    navigate(card.route, card.preset ? { state: { scenePreset: card.preset } } : undefined);
  };
  // ★先下架、再过权限,然后**同一份 cards** 既喂 hero 也喂卡墙 ——
  //   概览与被概览的东西必须数同一份,否则就是「概览说 5 个、卡墙只有 4 张」。
  const cards = DH_CARDS.filter(
    (c) => !c.hidden && (!c.moduleKey || c.moduleKey !== "ai_explosive_parse" || explosiveVisible)
  );

  return (
    <div className="space-y-6">
      {/* hero 吃**已过门**的 cards —— 没有情报权限时「从爆款开始」不在卡墙里,概览也要跟着少 */}
      <SceneHubHero hub="dh" cards={cards} />
      <section className="scene-wall">
        <SectionHead title="快捷创作" sub="口播出镜 · 声纹克隆 · 公式套产品" />
        <div ref={cardsRef} className="scene-grid">
          {cards.map((c, i) => (
            <SceneCapCard key={c.key} card={c} onOpen={() => open(c)} index={i} />
          ))}
        </div>
      </section>

      {/* 公共形象横排:平台策展安全资产(只读),点选即进口播工作台 */}
      {presets.length > 0 && (
        <section className="scene-wall">
          <SectionHead title="公共素材" sub="平台策展 · 开箱即用 · 不占克隆配额" />
          <div ref={presetsRef} className="flex gap-3 overflow-x-auto pb-1">
            {presets.map((a) => (
              <button
                key={a.id}
                type="button"
                data-preset-avatar={a.id}
                onClick={() =>
                  // 点公共形象带 presetAvatarId 直达(工作台预选该形象)。
                  // ★直指**照片线**:预置形象库是照片资产,真人口播线不提供这个来源。
                  navigate(DH_LINE_ROUTE.photo, {
                    state: { scenePreset: { kind: "dh_photo", presetAvatarId: a.id } },
                  })
                }
                className="group flex w-28 shrink-0 flex-col overflow-hidden rounded-xl border bg-card text-left transition-all hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-md"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted/40">
                  {a.thumbnailUrl ? (
                    <img
                      src={a.thumbnailUrl}
                      alt={a.label ?? a.id}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <UserSquare2 className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                  )}
                </div>
                <span className="truncate px-2 py-1.5 text-2xs font-medium">{a.label ?? a.id}</span>
              </button>
            ))}
          </div>
          {/* 授权红线条 */}
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-2xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            {/* B2 #13:0915 老板批示「真人授权后**公司共用**」,P 批漏改这句旧口径「仅限本人出片使用」 */}
            公共素材为平台策展安全资产;上传个人素材 /
            复刻个人声纹须本人授权并留存证,授权入库后全公司共用出片,跨公司不可用。
          </div>
        </section>
      )}
    </div>
  );
}
